import { Layout } from "@/components/layout/layout";
import {
  useListTournaments,
  useListVenues,
  useListMyRegistrations,
  getListMyRegistrationsQueryKey,
} from "@workspace/api-client-react";
import { TournamentCard } from "@/components/tournament-card";
import { useSeo } from "@/lib/seo";
import { useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays } from "lucide-react";
import { CardGridSkeleton, ListEmpty, ListErrorBlock } from "@/components/list-state";
import { useCurrentPlayer } from "@/hooks/useCurrentPlayer";

export default function Tournaments() {
  useSeo({
    title: "Upcoming Tournaments",
    description:
      "Browse all upcoming Interclub Poker Circuit tournaments. RSVP to pub poker games, see buy-ins, prize pools, and venues near you.",
    canonicalPath: "/tournaments",
  });
  const { player } = useCurrentPlayer();
  const [venueId, setVenueId] = useState<string>("all");

  const { data: tournamentsPage, isLoading: isLoadingTournaments, isError, error, refetch } = useListTournaments({
    venueId: venueId === "all" ? undefined : Number(venueId),
    limit: 200,
  });
  const tournaments = useMemo(
    () =>
      (tournamentsPage?.items ?? []).filter(
        (t) => t.status === "upcoming" || t.status === "active",
      ),
    [tournamentsPage],
  );
  const hasFilters = venueId !== "all";

  const { data: venues } = useListVenues();

  const { data: myRegistrations } = useListMyRegistrations({
    query: { enabled: !!player, queryKey: getListMyRegistrationsQueryKey() },
  });
  const registeredTournamentIds = useMemo(() => {
    const set = new Set<number>();
    if (!player) return set;
    for (const r of myRegistrations ?? []) {
      if (r.registrationStatus === "registered" || r.registrationStatus === "late") {
        set.add(r.tournamentId);
      }
    }
    return set;
  }, [myRegistrations, player]);

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-foreground tracking-tight">Circuit Schedule</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Find your next game. Filter by venue or check out the upcoming marquee events.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={venueId} onValueChange={setVenueId}>
            <SelectTrigger className="w-full sm:w-[250px]">
              <SelectValue placeholder="All Venues" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Venues</SelectItem>
              {venues?.map(v => (
                <SelectItem key={v.id} value={v.id.toString()}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoadingTournaments ? (
          <CardGridSkeleton count={6} />
        ) : isError ? (
          <ListErrorBlock error={error} onRetry={() => refetch()} title="Couldn't load tournaments" />
        ) : tournaments.length === 0 ? (
          <ListEmpty
            icon={<CalendarDays className="h-7 w-7" />}
            title={hasFilters ? "No upcoming or active tournaments at this venue" : "No tournaments scheduled"}
            description={hasFilters ? "Try clearing the venue filter to see more games." : "New tournaments will appear here as soon as they're announced."}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tournaments.map((tournament) => (
              <TournamentCard
                key={tournament.id}
                tournament={tournament}
                isRegistered={registeredTournamentIds.has(tournament.id)}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
